import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { SERVICES, BRAND } from "@/lib/brand";
import WhatsappIcon from "@/components/WhatsappIcon";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  phone: "",
  email: "",
  service: "transport",
  pickup_location: "",
  dropoff_location: "",
  vehicle_make: "",
  vehicle_model: "",
  preferred_date: "",
  message: "",
};

function buildWhatsappMessage(form) {
  const svc = SERVICES.find((s) => s.id === form.service);
  const lines = [
    "Hi MHJ Prestige Transport — I'd like a quote.",
    "",
    `Service: ${svc ? svc.title : form.service}`,
    `Name: ${form.name || "—"}`,
    `Phone: ${form.phone || "—"}`,
    form.email ? `Email: ${form.email}` : null,
    "",
    `Pickup: ${form.pickup_location || "—"}`,
    `Drop-off: ${form.dropoff_location || "—"}`,
    (form.vehicle_make || form.vehicle_model)
      ? `Vehicle: ${[form.vehicle_make, form.vehicle_model].filter(Boolean).join(" ")}`
      : null,
    form.preferred_date ? `Preferred date: ${form.preferred_date}` : null,
    form.message ? `\nDetails: ${form.message}` : null,
  ].filter(Boolean);
  return lines.join("\n");
}

export default function QuoteForm() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.name || !form.phone || !form.pickup_location || !form.dropoff_location) {
      toast.error("Please fill in name, phone, pickup and drop-off.");
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.pickup_location || !form.dropoff_location) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. We'll be in touch shortly.");
      setSubmitted(true);
      setForm(INITIAL);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not send enquiry. Please call us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const sendWhatsapp = () => {
    if (!validate()) return;
    const text = encodeURIComponent(buildWhatsappMessage(form));
    const url = `https://wa.me/${BRAND.whatsappRaw}?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp…");
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative bg-[#0A0A0A] py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-6">
            <span className="w-8 h-px bg-[#E60000]" />
            Quote · 02
          </div>
          <h2 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.0]">
            Tell us
            <br />
            the job.
            <br />
            <span className="text-stroke">We&apos;ll quote it.</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-base lg:text-lg leading-relaxed max-w-md">
            Fixed prices, no hidden surcharges. The more detail you share, the
            tighter we can get the number.
          </p>

          <div className="mt-10 space-y-4 text-sm text-zinc-400">
            <div className="flex items-baseline gap-4 border-t border-white/10 pt-4">
              <span className="text-[#E60000] font-display text-xl font-bold w-8">01</span>
              <span>Submit your details — takes a minute.</span>
            </div>
            <div className="flex items-baseline gap-4 border-t border-white/10 pt-4">
              <span className="text-[#E60000] font-display text-xl font-bold w-8">02</span>
              <span>We reply with a fixed quote, usually within an hour.</span>
            </div>
            <div className="flex items-baseline gap-4 border-t border-white/10 pt-4">
              <span className="text-[#E60000] font-display text-xl font-bold w-8">03</span>
              <span>Job booked, photos taken, vehicle delivered.</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div
              data-testid="quote-success"
              className="bg-[#050505] border border-[#E60000] p-10 lg:p-14"
            >
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
                Received
              </div>
              <h3 className="font-display text-white text-3xl lg:text-5xl font-extrabold mt-4">
                Thanks — we&apos;re on it.
              </h3>
              <p className="mt-4 text-zinc-400">
                Your enquiry has been logged. We&apos;ll be in touch by phone or
                email shortly. For anything urgent, call us directly.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="quote-new-button"
                className="btn-ghost mt-8"
              >
                Submit another enquiry <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <form
              data-testid="quote-form"
              onSubmit={submit}
              className="bg-[#050505] border border-white/10 p-8 lg:p-12 space-y-6"
            >
              <Row>
                <Field
                  label="Full name *"
                  testId="quote-input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="John Smith"
                  required
                />
                <Field
                  label="Phone *"
                  testId="quote-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="07xxx xxxxxx"
                  type="tel"
                  required
                />
              </Row>
              <Row>
                <Field
                  label="Email *"
                  testId="quote-input-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  type="email"
                  required
                />
                <SelectField
                  label="Service"
                  testId="quote-input-service"
                  value={form.service}
                  onChange={update("service")}
                  options={SERVICES.map((s) => ({ value: s.id, label: s.title }))}
                />
              </Row>
              <Row>
                <Field
                  label="Pickup location *"
                  testId="quote-input-pickup"
                  value={form.pickup_location}
                  onChange={update("pickup_location")}
                  placeholder="Town or postcode"
                  required
                />
                <Field
                  label="Drop-off location *"
                  testId="quote-input-dropoff"
                  value={form.dropoff_location}
                  onChange={update("dropoff_location")}
                  placeholder="Town or postcode"
                  required
                />
              </Row>
              <Row>
                <Field
                  label="Vehicle make"
                  testId="quote-input-make"
                  value={form.vehicle_make}
                  onChange={update("vehicle_make")}
                  placeholder="Porsche"
                />
                <Field
                  label="Vehicle model"
                  testId="quote-input-model"
                  value={form.vehicle_model}
                  onChange={update("vehicle_model")}
                  placeholder="911 GT3"
                />
              </Row>
              <Field
                label="Preferred date"
                testId="quote-input-date"
                value={form.preferred_date}
                onChange={update("preferred_date")}
                placeholder="ASAP / DD-MM-YYYY"
                type="text"
              />
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">
                  Job details
                </label>
                <textarea
                  data-testid="quote-input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  placeholder="Drives / non-runner? Low loader needed? Anything we should know."
                  className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000] transition-colors resize-none"
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                <p className="text-xs text-zinc-500 max-w-xs">
                  By submitting you agree we can contact you about your enquiry.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={sendWhatsapp}
                    data-testid="quote-whatsapp-button"
                    className="inline-flex items-center gap-2 px-6 py-[18px] border border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366] hover:text-black font-bold tracking-wide transition-colors"
                  >
                    <WhatsappIcon className="w-5 h-5" />
                    Send via WhatsApp
                  </button>
                  <button
                    type="submit"
                    data-testid="quote-submit-button"
                    disabled={submitting}
                    className="btn-primary disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Send enquiry <ArrowUpRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{children}</div>;
}

function Field({ label, value, onChange, placeholder, type = "text", required, testId }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">
        {label}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000] transition-colors"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">
        {label}
      </label>
      <select
        data-testid={testId}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E60000] transition-colors appearance-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg width='12' height='8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 8L0 0h12z' fill='%23E60000'/%3E%3C/svg%3E\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 16px center",
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-[#0A0A0A] text-white">
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
